import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { createHash, createHmac, randomBytes, timingSafeEqual } from "node:crypto";

const BUCKET = "realizacje";
const SIGNED_TTL = 60 * 60 * 24 * 7; // 7d
const TOKEN_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30d

function sessionSecret() {
  const secret = process.env.ELEKTRYK_ADMIN_SESSION_SECRET;
  if (!secret) throw new Error("ELEKTRYK_ADMIN_SESSION_SECRET is not set");
  return secret;
}

function signToken(expiresAt: number): string {
  const sig = createHmac("sha256", sessionSecret())
    .update(String(expiresAt))
    .digest("hex");
  return `${expiresAt}.${sig}`;
}

function verifyToken(token: string | undefined | null): boolean {
  if (!token) return false;
  const idx = token.indexOf(".");
  if (idx <= 0) return false;
  const expRaw = token.slice(0, idx);
  const sig = token.slice(idx + 1);
  const exp = Number(expRaw);
  if (!Number.isFinite(exp) || exp < Date.now()) return false;
  const expected = createHmac("sha256", sessionSecret())
    .update(String(exp))
    .digest("hex");
  const a = Buffer.from(sig, "hex");
  const b = Buffer.from(expected, "hex");
  return a.length === b.length && timingSafeEqual(a, b);
}

function readToken(): string | null {
  const h = getRequestHeader("x-elektryk-admin");
  return h ?? null;
}

function passwordMatches(input: string, expected: string): boolean {
  const a = createHash("sha256").update(String(input ?? ""), "utf8").digest();
  const b = createHash("sha256").update(expected, "utf8").digest();
  return a.length === b.length && timingSafeEqual(a, b);
}

function requireUnlocked() {
  if (!verifyToken(readToken())) throw new Error("Unauthorized");
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 60) || "realizacja";
}

// ---------- Auth ----------

export const elektrykAdminStatus = createServerFn({ method: "GET" }).handler(async () => {
  return { unlocked: verifyToken(readToken()) };
});

export const elektrykAdminUnlock = createServerFn({ method: "POST" })
  .inputValidator((data: { password: string }) => data)
  .handler(async ({ data }) => {
    const expected = process.env.ELEKTRYK_ADMIN_PASSWORD;
    if (!expected) throw new Error("ELEKTRYK_ADMIN_PASSWORD is not set");
    if (!passwordMatches(data.password, expected)) return { ok: false as const, token: null };
    const token = signToken(Date.now() + TOKEN_TTL_MS);
    return { ok: true as const, token };
  });

export const elektrykAdminLock = createServerFn({ method: "POST" }).handler(async () => {
  // Client clears the token from localStorage; nothing server-side to do.
  return { ok: true as const };
});

// ---------- Public ----------

async function signImages(
  client: any,
  rows: { id: string; url: string; alt: string | null; sort_order: number }[],
) {
  if (rows.length === 0) return [];
  const paths = rows.map((r) => r.url);
  const { data, error } = await client.storage.from(BUCKET).createSignedUrls(paths, SIGNED_TTL);
  if (error) throw error;
  return rows.map((r, i) => ({
    id: r.id,
    alt: r.alt,
    sort_order: r.sort_order,
    url: data?.[i]?.signedUrl ?? "",
  }));
}

export const elektrykPublicListRealizacje = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: rs, error } = await supabaseAdmin
    .from("realizacje")
    .select("id, slug, title, description, location, date_label, sort_order, created_at")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw error;
  const ids = (rs ?? []).map((r) => r.id);
  if (ids.length === 0) return [];
  const { data: imgs, error: ie } = await supabaseAdmin
    .from("realizacja_images")
    .select("id, realizacja_id, url, alt, sort_order")
    .in("realizacja_id", ids)
    .order("sort_order", { ascending: true });
  if (ie) throw ie;
  const signed = await signImages(supabaseAdmin, imgs ?? []);
  const byReal = new Map<string, typeof signed>();
  (imgs ?? []).forEach((row, i) => {
    const arr = byReal.get(row.realizacja_id) ?? [];
    arr.push(signed[i]);
    byReal.set(row.realizacja_id, arr);
  });
  return (rs ?? []).map((r) => ({ ...r, images: byReal.get(r.id) ?? [] }));
});

export const elektrykPublicGetRealizacja = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: r, error } = await supabaseAdmin
      .from("realizacje")
      .select("id, slug, title, description, location, date_label, created_at")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw error;
    if (!r) return null;
    const { data: imgs, error: ie } = await supabaseAdmin
      .from("realizacja_images")
      .select("id, url, alt, sort_order")
      .eq("realizacja_id", r.id)
      .order("sort_order", { ascending: true });
    if (ie) throw ie;
    const signed = await signImages(supabaseAdmin, imgs ?? []);
    return { ...r, images: signed };
  });

export const elektrykPublicListReviews = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("reviews")
    .select("id, author, author_role, rating, content, source, created_at")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
});

// ---------- Admin (CRUD) ----------

export const elektrykAdminListRealizacje = createServerFn({ method: "GET" }).handler(async () => {
  requireUnlocked();
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: rs, error } = await supabaseAdmin
    .from("realizacje")
    .select("id, slug, title, description, location, date_label, published, sort_order, created_at")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw error;
  const ids = (rs ?? []).map((r) => r.id);
  const { data: imgs } = ids.length
    ? await supabaseAdmin
        .from("realizacja_images")
        .select("id, realizacja_id, url, alt, sort_order")
        .in("realizacja_id", ids)
        .order("sort_order", { ascending: true })
    : { data: [] as any[] };
  const signed = await signImages(supabaseAdmin, imgs ?? []);
  const byReal = new Map<string, typeof signed>();
  (imgs ?? []).forEach((row, i) => {
    const arr = byReal.get(row.realizacja_id) ?? [];
    arr.push(signed[i]);
    byReal.set(row.realizacja_id, arr);
  });
  return (rs ?? []).map((r) => ({ ...r, images: byReal.get(r.id) ?? [] }));
});

export const elektrykAdminCreateRealizacja = createServerFn({ method: "POST" })
  .inputValidator((data: { title: string; description?: string; location?: string; date_label?: string }) => data)
  .handler(async ({ data }) => {
    requireUnlocked();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const title = data.title?.trim();
    if (!title) throw new Error("Tytuł jest wymagany");
    const base = slugify(title);
    const slug = `${base}-${randomBytes(3).toString("hex")}`;
    const { data: row, error } = await supabaseAdmin
      .from("realizacje")
      .insert({
        slug,
        title,
        description: data.description ?? null,
        location: data.location ?? null,
        date_label: data.date_label ?? null,
        published: true,
      })
      .select("id, slug")
      .single();
    if (error) throw error;
    return row;
  });

export const elektrykAdminUpdateRealizacja = createServerFn({ method: "POST" })
  .inputValidator((data: {
    id: string;
    title?: string;
    description?: string | null;
    location?: string | null;
    date_label?: string | null;
    published?: boolean;
  }) => data)
  .handler(async ({ data }) => {
    requireUnlocked();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: {
      title?: string;
      description?: string | null;
      location?: string | null;
      date_label?: string | null;
      published?: boolean;
    } = {};
    if (data.title !== undefined) patch.title = data.title.trim();
    if (data.description !== undefined) patch.description = data.description;
    if (data.location !== undefined) patch.location = data.location;
    if (data.date_label !== undefined) patch.date_label = data.date_label;
    if (data.published !== undefined) patch.published = data.published;
    const { error } = await supabaseAdmin.from("realizacje").update(patch).eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const elektrykAdminDeleteRealizacja = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    requireUnlocked();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: imgs } = await supabaseAdmin
      .from("realizacja_images")
      .select("url")
      .eq("realizacja_id", data.id);
    if (imgs && imgs.length) {
      await supabaseAdmin.storage.from(BUCKET).remove(imgs.map((i) => i.url));
    }
    const { error } = await supabaseAdmin.from("realizacje").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const elektrykAdminUploadImage = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    if (!(data instanceof FormData)) throw new Error("FormData required");
    return data;
  })
  .handler(async ({ data }) => {
    requireUnlocked();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const realizacjaId = String(data.get("realizacjaId") ?? "");
    const file = data.get("file");
    if (!realizacjaId || !(file instanceof File)) throw new Error("Brak pliku lub ID realizacji");
    if (file.size > 12 * 1024 * 1024) throw new Error("Maksymalny rozmiar pliku to 12 MB");
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
    const path = `${realizacjaId}/${Date.now()}-${randomBytes(4).toString("hex")}.${ext}`;
    const buf = Buffer.from(await file.arrayBuffer());
    const { error: upErr } = await supabaseAdmin.storage.from(BUCKET).upload(path, buf, {
      contentType: file.type || "image/jpeg",
      upsert: false,
    });
    if (upErr) throw upErr;
    const { data: maxRow } = await supabaseAdmin
      .from("realizacja_images")
      .select("sort_order")
      .eq("realizacja_id", realizacjaId)
      .order("sort_order", { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextSort = (maxRow?.sort_order ?? -1) + 1;
    const { data: row, error } = await supabaseAdmin
      .from("realizacja_images")
      .insert({ realizacja_id: realizacjaId, url: path, sort_order: nextSort })
      .select("id")
      .single();
    if (error) throw error;
    return { id: row.id };
  });

export const elektrykAdminDeleteImage = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    requireUnlocked();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("realizacja_images")
      .select("url")
      .eq("id", data.id)
      .maybeSingle();
    if (row?.url) await supabaseAdmin.storage.from(BUCKET).remove([row.url]);
    const { error } = await supabaseAdmin.from("realizacja_images").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });